import type { PropsWithChildren } from "react";

import { V2Tooltip } from "@stacklet/ui";

import { Link } from "./Link";

interface Props extends PropsWithChildren {
  to: string;
  description?: string;
  truncate?: boolean;
}

export function TooltipLink({
  to,
  children,
  description,
  truncate = false,
}: Props) {
  if (truncate) {
    return (
      // Outer span establishes the containing block so the flex parent
      // (e.g. a DataGrid cell) clips the link before its own overflow fires.
      <span className="block w-full min-w-0 overflow-hidden">
        <Link className="block w-full min-w-0" to={to} truncate>
          {description ? (
            <V2Tooltip className="break-all" title={description} asChild>
              {/* The tooltip trigger is width-constrained, so the ellipsis
                  must render here rather than on the <a> — the trigger is an
                  atomic inline box the <a>'s text-overflow cannot reach into.
                  This also keeps the tooltip anchored to the visible text. */}
              <span
                aria-label={description}
                className="block max-w-full truncate"
                data-testid={`tooltip-${to}`}
              >
                {children}
              </span>
            </V2Tooltip>
          ) : (
            <span className="inline align-baseline">{children}</span>
          )}
        </Link>
      </span>
    );
  }

  if (description) {
    return (
      <Link to={to}>
        <V2Tooltip className="break-all" title={description} asChild>
          <span
            aria-label={description}
            className="inline align-baseline"
            data-testid={`tooltip-${to}`}
          >
            {children}
          </span>
        </V2Tooltip>
      </Link>
    );
  }

  return (
    <Link to={to}>
      <span className="inline align-baseline">{children}</span>
    </Link>
  );
}
